import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { cn } from '@/lib/tailwind.utils'
import { usePlatform } from '@app/components/Guide/hooks/usePlatform'
import { Loader2, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

type Size = 'small' | 'medium'

export type SearchInputProps<T> = {
  value: string
  onChange: (v: string) => void

  items: T[]
  isLoading?: boolean
  minLength?: number

  getItemKey: (item: T) => string | number
  onSelectItem: (item: T) => void

  renderItem: (args: { item: T; query: string; isHighlighted: boolean }) => React.ReactNode

  placeholder?: string
  size?: Size

  onClear?: () => void
  className?: string
  inputClassName?: string
  iconClassName?: string
  shortcutLabel?: string
  onShortcut?: () => void
  onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export function SearchInput<T>({
  value,
  onChange,
  items,
  isLoading = false,
  minLength = 3,
  getItemKey,
  onSelectItem,
  renderItem,
  placeholder = 'Search',
  size = 'medium',
  onClear,
  shortcutLabel,
  onShortcut,
  onInputKeyDown,
}: SearchInputProps<T>) {
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [isSuggestionsHovered, setIsSuggestionsHovered] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<Array<HTMLDivElement | null>>([])

  const { isMac } = usePlatform()

  const trimmed = value.trim()
  const isValid = trimmed.length >= minLength
  const showSuggestions = (isInputFocused || isSuggestionsHovered) && trimmed.length > 0
  const showClearButton = value.length > 0
  const activeId = highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined

  const resolvedShortcutLabel = useMemo(() => {
    if (!shortcutLabel) return shortcutLabel

    return isMac
      ? shortcutLabel.replace(/ctrl/gi, '⌘')
      : shortcutLabel
          .replace(/cmd|command|⌘/gi, 'Ctrl')
          .replace(/ctrl\s*\+?\s*([a-z])/gi, 'Ctrl + $1')
  }, [shortcutLabel, isMac])

  useEffect(() => {
    suggestionsRef.current = new Array(items.length).fill(null)
    setHighlightedIndex(items.length > 0 ? 0 : -1)
  }, [items.length])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const isSearchShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k'

      if (!isSearchShortcut) return

      event.preventDefault()
      inputRef.current?.focus()
      onShortcut?.()
    }

    window.addEventListener('keydown', handleShortcut)

    return () => {
      window.removeEventListener('keydown', handleShortcut)
    }
  }, [onShortcut])

  const emptyState = useMemo(() => {
    if (!showSuggestions) return null
    if (!isValid) return 'Type more characters to see results'
    if (isLoading) return ''
    if (items.length === 0) return 'No matches found'
    return null
  }, [showSuggestions, isValid, isLoading, items.length])

  const handleClear = () => {
    onChange('')
    setHighlightedIndex(-1)
    onClear?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onInputKeyDown?.(e)

    if (e.defaultPrevented) return

    if (!showSuggestions) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, items.length - 1))
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      return
    }

    if (e.key === 'Enter') {
      if (!isValid) return
      const item = items[highlightedIndex]
      if (!item) return
      e.preventDefault()
      onSelectItem(item)
      setHighlightedIndex(-1)
      inputRef.current?.blur()
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  return (
    <div className={cn('relative w-full', size === 'small' ? 'max-w-sm' : 'max-w-md')}>
      {/* Input */}
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />

        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="search-suggestions"
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={cn(
            'pl-9',
            (showClearButton || resolvedShortcutLabel) && 'pr-16',
            size === 'small' ? 'h-8 text-sm' : 'h-10',
          )}
        />

        <div className="absolute right-3 flex items-center gap-1">
          {isLoading && showSuggestions && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
          {showClearButton ? (
            <X
              className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              onClick={handleClear}
            />
          ) : resolvedShortcutLabel ? (
            <Kbd className="hidden sm:inline-flex">{resolvedShortcutLabel}</Kbd>
          ) : null}
        </div>
      </div>

      {/* Dropdown */}
      {showSuggestions && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md"
          onMouseEnter={() => setIsSuggestionsHovered(true)}
          onMouseLeave={() => setIsSuggestionsHovered(false)}
        >
          <div id="search-suggestions" role="listbox" aria-busy={isLoading}>
            {!emptyState &&
              items.map((item, idx) => (
                <div
                  key={getItemKey(item)}
                  id={`option-${idx}`}
                  role="option"
                  aria-selected={highlightedIndex === idx}
                  ref={(el: HTMLDivElement | null) => (suggestionsRef.current[idx] = el)}
                  className={cn(
                    'px-3 py-2 cursor-pointer text-sm transition-colors',
                    highlightedIndex === idx
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground',
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => {
                    onSelectItem(item)
                    setHighlightedIndex(-1)
                    inputRef.current?.blur()
                  }}
                >
                  {renderItem({ item, query: trimmed, isHighlighted: highlightedIndex === idx })}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
